import type {
  AsanaInstallations,
  ConfluenceInstallations,
  JiraInstallations,
  LinearInstallations,
  NotionInstallations,
} from '@/generated/prisma/browser';
import type { CodeSessionEvent } from '@/lib/code-sessions/types';
import type { NangoConnector } from '@/lib/nango/config';
import type { SlackConnection } from '@/lib/nango/types';
import type { DesktopNotificationQuickAction } from '@/lib/notifications/web-app/desktop-quick-action';
import type { PrV2ChangedDomain } from '@/lib/pr-v2/realtime-shared';
import type { CheckRunEvent } from '@octokit/webhooks-types';

export type IntegrationConnectionProvider = NangoConnector | 'confluence';

export type IntegrationConnectionRefreshFailure = {
  provider: IntegrationConnectionProvider;
  connectionId: string;
  endUserId: string;
  message: string | null;
  errorType: string | null;
};

type PullRequestRealtimeRef = {
  owner: string;
  repo: string;
  number: number;
};

type RepoRealtimeRef = {
  owner: string;
  repo: string;
};

type PublicPrV2RealtimePayload = {
  type: 'public_pr_v2_update';
  changedDomains: PrV2ChangedDomain[];
  pr: PullRequestRealtimeRef;
};

type PublicReviewStatusRealtimePayload = {
  type: 'public_review_status_update';
  status: ReviewRunStatus;
  runKind: ReviewRunKind;
  violationCount: number;
  progressPercentage?: number;
  filesProcessed?: number;
  totalFiles?: number;
  pr: PullRequestRealtimeRef;
};

type PublicCheckRunRealtimePayload = {
  type: 'public_check_run_update';
  repo: RepoRealtimeRef;
  checkRun: {
    headSha: string;
    name: string;
    status: CheckRunEvent['check_run']['status'];
    conclusion: CheckRunEvent['check_run']['conclusion'];
  };
};

/**
 * Constant for realtime event keys.
 * This provides type safety when accessing the REALTIME object.
 */
export const REALTIME_KEYS = {
  PR_V2_UPDATE: 'PR_V2_UPDATE',
  PUBLIC_PR_V2_UPDATE: 'PUBLIC_PR_V2_UPDATE',
  INSTALLATION_UPDATE: 'INSTALLATION_UPDATE',
  DESKTOP_NOTIFICATION: 'DESKTOP_NOTIFICATION',
  NOTIFICATION_UPDATE: 'NOTIFICATION_UPDATE',
  REVIEW_STATUS_UPDATE: 'REVIEW_STATUS_UPDATE',
  PUBLIC_REVIEW_STATUS_UPDATE: 'PUBLIC_REVIEW_STATUS_UPDATE',
  CHECK_RUN_UPDATE: 'CHECK_RUN_UPDATE',
  PUBLIC_CHECK_RUN_UPDATE: 'PUBLIC_CHECK_RUN_UPDATE',
  SLACK_CONNECTION_UPDATE: 'SLACK_CONNECTION_UPDATE',
  LINEAR_CONNECTION_UPDATE: 'LINEAR_CONNECTION_UPDATE',
  JIRA_CONNECTION_UPDATE: 'JIRA_CONNECTION_UPDATE',
  ASANA_CONNECTION_UPDATE: 'ASANA_CONNECTION_UPDATE',
  NOTION_CONNECTION_UPDATE: 'NOTION_CONNECTION_UPDATE',
  CONFLUENCE_CONNECTION_UPDATE: 'CONFLUENCE_CONNECTION_UPDATE',
  LEARNINGS_IMPORT_COMPLETE: 'LEARNINGS_IMPORT_COMPLETE',
  CODE_SESSION_EVENT: 'CODE_SESSION_EVENT',
  FIX_SESSION_UPDATE: 'FIX_SESSION_UPDATE',
} as const;

/**
 * Type for realtime event keys derived from the REALTIME_KEYS object.
 */
export type RealtimeEventKey = (typeof REALTIME_KEYS)[keyof typeof REALTIME_KEYS];

/**
 * Simplified realtime event system.
 *
 * To add a new event type:
 * 1. Add a new entry to the REALTIME_KEYS object
 * 2. Add a corresponding entry to this object with:
 *    - eventType: The event type string
 *    - channelPrefix: The channel prefix string
 *    - payloadType: The TypeScript interface for the payload
 *
 * This single definition will automatically generate all necessary types and constants.
 */
export const REALTIME = {
  [REALTIME_KEYS.PR_V2_UPDATE]: {
    eventType: 'pr_v2_update',
    channelPrefix: 'pr_v2_updates',
    payloadType: {} as {
      type: 'pr_v2_update';
      webhookEvent: string;
      action: string;
      deliveryId: string | null;
      changedDomains: PrV2ChangedDomain[];
      installationId: number;
      pr: {
        owner: string;
        repo: string;
        number: number;
      };
    },
  },

  [REALTIME_KEYS.PUBLIC_PR_V2_UPDATE]: {
    eventType: 'public_pr_v2_update',
    channelPrefix: 'public_pr_v2_updates',
    payloadType: {} as PublicPrV2RealtimePayload,
  },

  [REALTIME_KEYS.INSTALLATION_UPDATE]: {
    eventType: 'installation_update',
    channelPrefix: 'installation_updates',
    payloadType: {} as {
      type: 'installation_update';
      action: 'created' | 'deleted';
      installationId: number;
    },
  },

  [REALTIME_KEYS.DESKTOP_NOTIFICATION]: {
    eventType: 'desktop-notification',
    channelPrefix: 'notifications',
    payloadType: {} as {
      type: 'desktop-notification';
      payload: {
        /** Assigned when published, so a live event and a catch-up show it once. */
        id: string;
        /** ISO time it was published. */
        sentAt: string;
        title: string;
        body: string;
        link: string;
        /** Offered as notification buttons or inline reply in the desktop app. */
        quickAction: DesktopNotificationQuickAction | null;
        /** Shown only by the desktop app; a browser tab does not fall back to a web notification. */
        desktopOnly: boolean;
      };
    },
  },

  [REALTIME_KEYS.NOTIFICATION_UPDATE]: {
    eventType: 'notification-update',
    channelPrefix: 'notification_updates',
    payloadType: {} as {
      type: 'notification-update';
      userId: string;
    },
  },

  [REALTIME_KEYS.REVIEW_STATUS_UPDATE]: {
    eventType: 'review_status_update',
    channelPrefix: 'review_status_updates',
    payloadType: {} as {
      type: 'review_status_update';
      reviewRunId: string;
      traceId: string;
      status: ReviewRunStatus;
      runKind: ReviewRunKind;
      ruleId?: string;
      ruleTitle?: string;
      violationCount: number;
      createdAt: string;
      updatedAt: string;
      progressPercentage?: number;
      progressMessage?: string;
      filesProcessed?: number;
      totalFiles?: number;
      pr: {
        owner: string;
        repo: string;
        number: number;
      };
    },
  },

  [REALTIME_KEYS.PUBLIC_REVIEW_STATUS_UPDATE]: {
    eventType: 'public_review_status_update',
    channelPrefix: 'public_review_status_updates',
    payloadType: {} as PublicReviewStatusRealtimePayload,
  },

  [REALTIME_KEYS.CHECK_RUN_UPDATE]: {
    eventType: 'check_run_update',
    channelPrefix: 'check_run_updates',
    payloadType: {} as {
      type: 'check_run_update';
      checkRun: CheckRunEvent['check_run'];
    },
  },

  [REALTIME_KEYS.PUBLIC_CHECK_RUN_UPDATE]: {
    eventType: 'public_check_run_update',
    channelPrefix: 'public_check_run_updates',
    payloadType: {} as PublicCheckRunRealtimePayload,
  },

  [REALTIME_KEYS.SLACK_CONNECTION_UPDATE]: {
    eventType: 'slack_connection_update',
    channelPrefix: 'slack_connection_updates',
    payloadType: {} as {
      type: 'slack_connection_update';
      slackConnection: SlackConnection | null;
      refreshFailure?: IntegrationConnectionRefreshFailure;
    },
  },

  [REALTIME_KEYS.LINEAR_CONNECTION_UPDATE]: {
    eventType: 'linear_connection_update',
    channelPrefix: 'linear_connection_updates',
    payloadType: {} as {
      type: 'linear_connection_update';
      linearConnection: LinearInstallations | null;
      refreshFailure?: IntegrationConnectionRefreshFailure;
    },
  },
  [REALTIME_KEYS.JIRA_CONNECTION_UPDATE]: {
    eventType: 'jira_connection_update',
    channelPrefix: 'jira_connection_updates',
    payloadType: {} as {
      type: 'jira_connection_update';
      jiraConnection: JiraInstallations | null;
      refreshFailure?: IntegrationConnectionRefreshFailure;
    },
  },
  [REALTIME_KEYS.ASANA_CONNECTION_UPDATE]: {
    eventType: 'asana_connection_update',
    channelPrefix: 'asana_connection_updates',
    payloadType: {} as {
      type: 'asana_connection_update';
      asanaConnection: AsanaInstallations | null;
      refreshFailure?: IntegrationConnectionRefreshFailure;
    },
  },
  [REALTIME_KEYS.NOTION_CONNECTION_UPDATE]: {
    eventType: 'notion_connection_update',
    channelPrefix: 'notion_connection_updates',
    payloadType: {} as {
      type: 'notion_connection_update';
      notionConnection: NotionInstallations | null;
      refreshFailure?: IntegrationConnectionRefreshFailure;
    },
  },
  [REALTIME_KEYS.CONFLUENCE_CONNECTION_UPDATE]: {
    eventType: 'confluence_connection_update',
    channelPrefix: 'confluence_connection_updates',
    payloadType: {} as {
      type: 'confluence_connection_update';
      confluenceConnection: ConfluenceInstallations | null;
      refreshFailure?: IntegrationConnectionRefreshFailure;
    },
  },

  [REALTIME_KEYS.LEARNINGS_IMPORT_COMPLETE]: {
    eventType: 'learnings_import_complete',
    channelPrefix: 'learnings_import_complete_updates',
    payloadType: {} as {
      type: 'learnings_import_complete';
      installationId: number;
      importId: string;
      inserted: number;
      skipped: number;
      repositoryIds: number[];
    },
  },

  [REALTIME_KEYS.CODE_SESSION_EVENT]: {
    eventType: 'code-session-event',
    channelPrefix: 'code_session',
    payloadType: {} as {
      type: 'code-session-event';
      sessionId: string;
      /** Batched events — one payload per flush window, seq-ordered. */
      events: CodeSessionEvent[];
    },
  },

  [REALTIME_KEYS.FIX_SESSION_UPDATE]: {
    eventType: 'fix_session_update',
    channelPrefix: 'fix_session_updates',
    // A hint that names no session: the installation topic admits members who may not list the
    // session's repository, so subscribers refetch the access-checked list instead.
    payloadType: {} as {
      type: 'fix_session_update';
      installationId: number;
    },
  },
} as const;

export type ReviewRunStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'timeout'
  | 'cancelled'
  | 'skipped';

export const REVIEW_RUN_KIND = {
  GENERAL: 'general',
  RULE: 'rule',
  BATCH: 'batch',
} as const;

export type ReviewRunKind = (typeof REVIEW_RUN_KIND)[keyof typeof REVIEW_RUN_KIND];

// Derive types from the REALTIME object
export type RealtimeEventConfig = (typeof REALTIME)[RealtimeEventKey];
export type RealtimeEventType = RealtimeEventConfig['eventType'];
export type RealtimeChannelPrefix = RealtimeEventConfig['channelPrefix'];

// Type for channel names
export type RealtimeChannel = `${RealtimeChannelPrefix}:${string}` | RealtimeChannelPrefix;

// Helper type to get payload type for a specific event type
export type PayloadForEventType<T extends RealtimeEventKey> = (typeof REALTIME)[T]['payloadType'];

// Union type of all possible realtime events
export type RealtimeEvent = RealtimeEventConfig['payloadType'];

function normalizeRealtimeRepoSegment(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Canonical repo-scoped realtime topic id used across legacy, PR v2, and public PR channels.
 */
export function buildRealtimePullRequestId(
  owner: string,
  repo: string,
  number: number | string,
): string {
  return `${normalizeRealtimeRepoSegment(owner)}/${normalizeRealtimeRepoSegment(repo)}/${number}`;
}

/**
 * Canonical repo+commit realtime topic id for check-run style channels.
 */
export function buildRealtimeRepoCommitId(owner: string, repo: string, commitish: string): string {
  return `${normalizeRealtimeRepoSegment(owner)}/${normalizeRealtimeRepoSegment(repo)}/${normalizeRealtimeRepoSegment(commitish)}`;
}

/**
 * Helper function to construct a channel name with the proper format.
 * @param eventKey - The event key from REALTIME object
 * @param id - The dynamic identifier (required for all channels)
 * @returns Properly formatted channel name
 */
export function getChannelName<T extends RealtimeEventKey>(
  eventKey: T,
  id: string | number,
): RealtimeChannel {
  const prefix = REALTIME[eventKey].channelPrefix;
  return `${prefix}:${id}` as RealtimeChannel;
}

/**
 * Helper function to get the event type for a given channel.
 * @param channel - The channel name
 * @returns The corresponding event type or undefined if not found
 */
export function getEventTypeForChannel(channel: string): RealtimeEventType | undefined {
  const prefix = channel.includes(':') ? channel.split(':')[0] : channel;

  for (const key in REALTIME) {
    const config = REALTIME[key as RealtimeEventKey];
    if (config.channelPrefix === prefix) {
      return config.eventType;
    }
  }

  console.error(`[Realtime] Invalid channel: ${channel}`);
  return undefined;
}
